import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import { ListingProps, ListResponse } from "types/listing";
import {
  BuyRewardsFields,
  RedeemedReward,
  RewardCategoryFields,
  RewardCategoryListItem,
  RewardCoupon,
  RewardFieldsBE,
} from "types/rewards";
import { getEnvVariable } from "utilities/env";

export function useReward() {
  const { code: tenant } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const addReward = async (values: any) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/reward`,
      {
        body: JSON.stringify({ reward: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editReward = async (rewardID: string, { ...payload }) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/reward/${rewardID}`,
      {
        body: JSON.stringify({ reward: payload }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response;

    if (response.status === 204) {
      return { data };
    } else {
      throw await response.json();
    }
  };

  const setPhoto = async (rewardID: string, file: any) => {
    const formData = new FormData();
    formData.append("photo[file]", file);
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/reward/${rewardID}/photo`,
      { body: formData, method: "post" },
    );
    const data = await response.text();
    if (response.ok) {
      return data ? JSON.parse(data) : {};
    } else {
      throw data ? JSON.parse(data) : {};
    }
  };

  const removePhoto = async (rewardID: string, photoId: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/reward/${rewardID}/photo/${photoId}`,
      { method: "delete" },
    );
    const data = await response.text();
    if (response.ok) {
      return data ? JSON.parse(data) : {};
    } else {
      throw data ? JSON.parse(data) : {};
    }
  };

  const useRewardData = (rewardID?: string): SWRResponse<RewardFieldsBE> => {
    return useSWR(
      () =>
        tenant && rewardID
          ? `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/reward/${rewardID}`
          : null,
      fetcher,
    );
  };

  const useRewardCategoriesList = (props?: ListingProps) => {
    return useSWR(
      () =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/rewardCategory${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteRewardCategoriesList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<RewardCategoryListItem>>(
      (index) =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/rewardCategory${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const useRewardCategoryData = (rewardCategoryID: string) => {
    return useSWR(
      () =>
        tenant && rewardCategoryID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/rewardCategory/${rewardCategoryID}`
          : null,
      fetcher,
    );
  };

  const addRewardCategory = async (values: RewardCategoryFields) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/rewardCategory`,
      {
        body: JSON.stringify({ reward_category: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editRewardCategory = async (
    rewardCategoryID: string,
    { ...payload }: RewardCategoryFields,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/rewardCategory/${rewardCategoryID}`,
      {
        body: JSON.stringify({ reward_category: payload }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response;

    if (response.status === 200) {
      return { data };
    } else {
      throw await response.json();
    }
  };

  const activateRewardCategory = async (rewardCategoryID: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/rewardCategory/${rewardCategoryID}/active`,
      {
        body: JSON.stringify({ active: true }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const deactivateRewardCategory = async (rewardCategoryID: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/rewardCategory/${rewardCategoryID}/active`,
      {
        body: JSON.stringify({ active: false }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useCouponsList = (
    rewardId: string,
    props?: ListingProps,
  ): SWRResponse<ListResponse<RewardCoupon>> => {
    return useSWR(
      () =>
        tenant && rewardId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/reward/${rewardId}/coupon${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const removeCoupon = async (couponId: string, rewardId: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/reward/${rewardId}/coupon/${couponId}`,
      { method: "delete" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const addCoupon = async (coupons: string, rewardId: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/reward/${rewardId}/coupon`,
      {
        body: JSON.stringify({
          coupons: [coupons],
        }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const claimReward = async (
    rewardId: string,
    rewardOptions: BuyRewardsFields,
  ) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/reward/${rewardId}/buy`,
      {
        body: JSON.stringify(rewardOptions),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const useRewardList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<RewardFieldsBE>> => {
    return useSWR(
      () =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/reward${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteRewardList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<RewardFieldsBE>>(
      (index) =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/reward${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const useRewardsFulfillmentList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<RedeemedReward>> => {
    return useSWR(
      () =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/redemption${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const changeRedemptionStatus = async (issuedReward: string, values: any) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/redemption/${issuedReward}/status`,
      {
        body: JSON.stringify(values),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useRedemptionHistory = (issuedReward: string, props?: ListingProps) => {
    return useSWR(
      () =>
        tenant && issuedReward
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/redemption/${issuedReward}/status/history${querystring(
              props,
            )}`
          : null,
      fetcher,
    );
  };

  const useRedemptionDetails = (issuedReward: string) => {
    return useSWR(
      () =>
        tenant && issuedReward
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/redemption/${issuedReward}`
          : null,
      fetcher,
    );
  };

  const addRewardBatch = async (values: any[]) => {
    if (!tenant) return;

    const responses = await Promise.all(
      values.map(({ rewardId, name, ...rest }) =>
        rewardId
          ? new Promise((resolve) =>
              resolve({
                rewardId,
                ...rest,
              }),
            )
          : fetcher(`${getEnvVariable("REACT_APP_API_URL")}/${tenant}/reward`, {
              body: JSON.stringify({
                reward: { ...rest, active: false },
              }),
              headers: { "Content-Type": "application/json" },
              method: "POST",
            }),
      ),
    )
      .then((results) =>
        Promise.all(results.map((r) => (r.json ? r.json() : r))),
      )
      .then((resp) =>
        resp.map((r, i) => ({
          ...r,
          data: values[i],
        })),
      );

    return responses;
  };

  const importCoupons = async (rewardId: string, file?: string | Blob) => {
    const formData = new FormData();
    file && formData.append("import[file]", file);
    rewardId && formData.append("import[additionalData][rewardId]", rewardId);
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/import/rewardCoupon`,
      { body: formData, method: "post" },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  return {
    activateRewardCategory,
    addCoupon,
    addReward,
    addRewardBatch,
    addRewardCategory,
    changeRedemptionStatus,
    claimReward,
    deactivateRewardCategory,
    editReward,
    editRewardCategory,
    importCoupons,
    removeCoupon,
    removePhoto,
    setPhoto,
    useCouponsList,
    useRedemptionDetails,
    useRedemptionHistory,
    useRewardCategoriesList,
    useInfiniteRewardCategoriesList,
    useRewardCategoryData,
    useRewardData,
    useRewardList,
    useRewardsFulfillmentList,
    useInfiniteRewardList,
  };
}
